import {
  Float,
  Group,
  HStack,
  Icon,
  IconButton,
  Popover,
  Portal,
  Separator,
  SimpleGrid,
  Stack,
  Text
} from '@chakra-ui/react'
import type { ConditionalValue } from '@chakra-ui/react'
import type { FC } from 'react'
import { memo, useCallback, useMemo, useRef, useState } from 'react'
import { FiChevronRight } from 'react-icons/fi'
import { useNavigate } from 'react-router-dom'

import { Button } from '@app/components/ui/button'
import { FileUploadRoot, FileUploadTrigger } from '@app/components/ui/file-button'
import { Tooltip } from '@app/components/ui/tooltip'
import useGetObjects from '@app/hooks/useGetObjects'
import useMapHotkeys from '@app/hooks/useMapHotkeys'
import useMapNodeCreators from '@app/hooks/useMapNodeCreators'
import useMousePosition from '@app/hooks/useMousePosition'
import BasicTypeModal from '@app/pages/strategies/components/nodes/components/basicType/basicTypeModal'
import { iconMap as basicCardIconMap } from '@app/pages/strategies/components/nodes/components/basicType/customIcons'
import KeyboardShortcutsTooltip from '@app/shared/keyboardShortcutsTooltip'
import ICON_MAP from '@app/utils/iconMap'

export const buttonStyles = {
  fontSize: 'xl',
  borderRadius: 0,
  gap: 0,
  size: 'xl' as ConditionalValue<'sm' | 'md' | 'lg' | 'xl' | '2xl' | '2xs' | 'xs'>,
  variant: 'ghost' as ConditionalValue<'outline' | 'ghost' | 'solid' | 'subtle' | 'surface' | 'plain'>,
  colorPalette: 'gray'
}

const tooltipPlacement = 'right'

const noteLabel = <KeyboardShortcutsTooltip title="Note" shortcut="N" />
const imageLabel = <KeyboardShortcutsTooltip title="Image" shortcut="I" />
const commentLabel = <KeyboardShortcutsTooltip title="Comment" shortcut="T" />

type Props = {
  strategyId: string
}

const AddCardMenu: FC<Props> = ({ strategyId }) => {
  const navigate = useNavigate()

  const imageInputRef = useRef<HTMLInputElement>(null)
  const { createCommentThread, createNote, createImage } = useMapNodeCreators(strategyId)
  useMapHotkeys(strategyId, imageInputRef)
  const getLatestMousePosition = useMousePosition()

  const cardTypes = useGetObjects('cardType')
  const sortedCardTypes = useMemo(() => cardTypes.sort((a, b) => a.name.localeCompare(b.name)), [cardTypes])
  const shownCardTypes = useMemo(() => sortedCardTypes.filter((ct) => ct.showInMenu), [sortedCardTypes])

  const [manageBasicTypesOpen, setManageBasicTypesOpen] = useState(false)
  const [basicTypesOpen, setBasicTypesOpen] = useState(false)

  document.onpaste = useCallback(
    (evt) => {
      const dataTransferred = evt.clipboardData
      const mousePosition = getLatestMousePosition()

      if (dataTransferred.files.length > 0) {
        createImage(dataTransferred.files[0], mousePosition)
      }
    },
    [createImage, getLatestMousePosition]
  )

  const openExistingObjectDrawer = useCallback(
    (type) => {
      navigate(`add/${type}`)
    },
    [navigate]
  )

  return (
    <>
      <Group gap={0} orientation="vertical">
        <Tooltip
          content={<KeyboardShortcutsTooltip title="Metric" shortcut="M" />}
          positioning={{ placement: tooltipPlacement }}
          showArrow
        >
          <IconButton
            {...buttonStyles}
            aria-label="metric"
            data-toolbar-button-id="metric"
            onClick={() => openExistingObjectDrawer('metric')}
          >
            <ICON_MAP.Metric />
          </IconButton>
        </Tooltip>

        <Tooltip
          content={<KeyboardShortcutsTooltip title="Work" shortcut="W" />}
          positioning={{ placement: tooltipPlacement }}
          showArrow
        >
          <IconButton
            {...buttonStyles}
            aria-label="work"
            data-toolbar-button-id="work"
            onClick={() => openExistingObjectDrawer('entity')}
          >
            <ICON_MAP.Work />
          </IconButton>
        </Tooltip>
        <Tooltip content="Submap" positioning={{ placement: tooltipPlacement }} showArrow>
          <IconButton
            {...buttonStyles}
            aria-label="map"
            data-toolbar-button-id="map"
            onClick={() => openExistingObjectDrawer('strategy')}
          >
            <ICON_MAP.StrategyMap />
          </IconButton>
        </Tooltip>
        <Tooltip content="Cards" positioning={{ placement: tooltipPlacement }}>
          <Popover.Root
            positioning={{ placement: 'right-start', gutter: 2 }}
            open={basicTypesOpen}
            onOpenChange={(e) => setBasicTypesOpen(e.open)}
            size="lg"
          >
            <Popover.Trigger asChild>
              <IconButton
                {...buttonStyles}
                aria-label="cards"
                data-toolbar-button-id="cards"
                onClick={() => setBasicTypesOpen(!basicTypesOpen)}
              >
                <ICON_MAP.BasicCardList />
                <Float placement="middle-end" offset={1.5}>
                  <Icon boxSize={3} color="fg.subtle">
                    <FiChevronRight />
                  </Icon>
                </Float>
              </IconButton>
            </Popover.Trigger>
            <Portal>
              <Popover.Positioner>
                <Popover.Content>
                  <Popover.CloseTrigger />
                  <Popover.Arrow>
                    <Popover.ArrowTip />
                  </Popover.Arrow>
                  <Popover.Body>
                    <Stack>
                      <SimpleGrid columns={2} gap={2}>
                        {shownCardTypes?.map((type) => {
                          const IconComponent = basicCardIconMap[type.icon] || ICON_MAP.BasicCard

                          return (
                            <Button
                              key={type.id}
                              aria-label={type.name}
                              data-toolbar-button-id={`card-${type.name}`}
                              onClick={() => {
                                setBasicTypesOpen(false)
                                openExistingObjectDrawer(`basicCard/${type.id}`)
                              }}
                              variant="outline"
                            >
                              <HStack align="center">
                                <IconComponent />
                                <Text fontSize="xs">{type.name}</Text>
                              </HStack>
                            </Button>
                          )
                        })}
                      </SimpleGrid>
                      <Button
                        w="full"
                        onClick={() => {
                          setBasicTypesOpen(false)
                          setManageBasicTypesOpen(true)
                        }}
                        data-toolbar-button-id="manage-cards"
                        aria-label="manage card types"
                      >
                        Manage card types
                      </Button>
                    </Stack>
                  </Popover.Body>
                </Popover.Content>
              </Popover.Positioner>
            </Portal>
          </Popover.Root>
        </Tooltip>
      </Group>
      <Separator />
      <Group gap={0} orientation="vertical">
        <Tooltip content={noteLabel} positioning={{ placement: tooltipPlacement }} showArrow>
          <IconButton {...buttonStyles} data-toolbar-button-id="note" aria-label="note" onClick={createNote}>
            <ICON_MAP.Note />
          </IconButton>
        </Tooltip>

        <Tooltip content={imageLabel} positioning={{ placement: tooltipPlacement }} showArrow>
          <FileUploadRoot accept="image/*" onFileAccept={(e) => createImage(e.files[0])}>
            <FileUploadTrigger asChild>
              <IconButton {...buttonStyles} aria-label="image">
                <ICON_MAP.Image />
              </IconButton>
            </FileUploadTrigger>
          </FileUploadRoot>
        </Tooltip>

        <Tooltip content={commentLabel} positioning={{ placement: tooltipPlacement }} showArrow>
          <IconButton
            {...buttonStyles}
            data-toolbar-button-id="comment"
            aria-label="comment"
            onClick={createCommentThread}
          >
            <ICON_MAP.Comments />
          </IconButton>
        </Tooltip>
      </Group>
      <BasicTypeModal
        cardTypes={cardTypes}
        open={manageBasicTypesOpen}
        onOpenChange={(e) => {
          setManageBasicTypesOpen(e.open)
        }}
      />
    </>
  )
}

AddCardMenu.displayName = 'AddCardMenu'

export default memo(AddCardMenu)
